import React from 'react';

interface Column {
  key: string;
  label: string;
}

interface DataTableProps {
  data: any[];
  columns: Column[];
  title: string;
  isLoading?: boolean;
  emptyMessage?: string;
}

const DataTable: React.FC<DataTableProps> = ({ 
  data, 
  columns, 
  title, 
  isLoading = false,
  emptyMessage = "No data available" 
}) => {
  if (isLoading) {
    return (
      <div className="card">
        <h3 className="text-lg font-montserrat font-semibold text-northwood-navy mb-4">
          {title}
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="text-neutral-medium-gray">Loading...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="card">
        <h3 className="text-lg font-montserrat font-semibold text-northwood-navy mb-4">
          {title}
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="text-neutral-medium-gray">{emptyMessage}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-montserrat font-semibold text-northwood-navy">
          {title}
        </h3>
        <span className="text-sm text-neutral-medium-gray">
          {data.length} {data.length === 1 ? 'record' : 'records'}
        </span>
      </div>
      
      <div className="overflow-x-auto max-h-96 overflow-y-auto border border-neutral-silver rounded-md">
        <table className="min-w-full divide-y divide-neutral-silver">
          <thead className="bg-neutral-light-gray sticky top-0">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-4 py-3 text-left text-xs font-montserrat font-semibold text-neutral-dark-gray uppercase tracking-wider"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-light-gray">
            {data.map((row, index) => (
              <tr key={index} className="hover:bg-neutral-light-gray transition-colors duration-200">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="px-4 py-3 whitespace-nowrap text-sm text-neutral-dark-gray"
                  >
                    {Array.isArray(row[column.key]) 
                      ? row[column.key].join(', ') 
                      : (() => {
                          const value = row[column.key];
                          if (value === null || value === undefined || value === '' || 
                              (typeof value === 'number' && isNaN(value)) || 
                              (typeof value === 'string' && (value.toLowerCase() === 'nan' || value === 'null'))) {
                            return '-';
                          }
                          return value;
                        })()
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;